import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { ensureOperatorsSuccess } from "../lib/operatorsApi";
import { useAuthStore } from "../stores/authStore";

/**
 * Protected API call to operators-query Edge Function
 */
async function queryOperators<T>(
  token: string | null,
  action: string
): Promise<T> {
  if (!token) {
    throw new Error("Not authenticated");
  }

  const { data, error } = await supabase.functions.invoke("operators-query", {
    body: { action, params: {} },
    headers: {
      "X-Portale-Token": token,
    },
  });

  return ensureOperatorsSuccess<T>(data, error);
}

export interface CategoryTypeGroup {
  category: string;
  types: string[];
}

/**
 * Get unique values for filters (categories, types, regions)
 * Protected by authentication
 */
export function useFilterOptions() {
  const { token, isAuthenticated } = useAuthStore();

  const categoriesQuery = useQuery<string[]>({
    queryKey: ["filter-options", "categories"],
    queryFn: () => queryOperators<string[]>(token, "get_categories"),
    staleTime: 0, // Always refetch to get latest categories
    enabled: isAuthenticated && !!token,
  });

  const typesQuery = useQuery<string[]>({
    queryKey: ["filter-options", "types"],
    queryFn: () => queryOperators<string[]>(token, "get_types"),
    staleTime: 300000,
    enabled: isAuthenticated && !!token,
  });

  const regionsQuery = useQuery<string[]>({
    queryKey: ["filter-options", "regions"],
    queryFn: () => queryOperators<string[]>(token, "get_regions"),
    staleTime: 300000,
    enabled: isAuthenticated && !!token,
  });

  // Hierarchical category-type data
  const categoryTypesQuery = useQuery<CategoryTypeGroup[]>({
    queryKey: ["filter-options", "category-types"],
    queryFn: () => queryOperators<CategoryTypeGroup[]>(token, "get_category_types"),
    staleTime: 300000,
    enabled: isAuthenticated && !!token,
  });

  return {
    categories: categoriesQuery.data ?? [],
    types: typesQuery.data ?? [],
    regions: regionsQuery.data ?? [],
    categoryTypes: categoryTypesQuery.data ?? [],
    isLoading: categoriesQuery.isLoading || typesQuery.isLoading || regionsQuery.isLoading || categoryTypesQuery.isLoading,
    error: categoriesQuery.error || typesQuery.error || regionsQuery.error || categoryTypesQuery.error,
  };
}
